import React, { useEffect, useMemo, useRef, useState } from "react";
import Table, { ColumnType } from "antd/lib/table";
import { openNotificationWithIcon } from "../../utils";
import "./feedbacks.scss";
import { useFetchDocuments } from "../../hooks/useFetchDocuments";
import { Empty } from "antd";
import type { TableProps } from "antd";
import "../../assets/sass/layout/search.scss";
import { useDispatch, useSelector } from "react-redux";
import { setFeedbackDateRange } from "../../store";
import moment from "moment";
import { MetroFilter } from "../MetroFilters";
import { feedbackService } from "../../api/feedback";
import DateRangePicker from "../../containers/feedback-data/DateRangePicker";
import ExportToExcel from "../ExportToExcel";

interface Props {
  projectId?: number;
  loading?: boolean;
}

interface IFeedbackAnalyticsDetailed {
  key: React.Key;
  metro: string;
  totalDocuments: number;
  totalPlaceholders: number;
  lowConf: number;
  lowConfPercentage: number;
  renaming: number;
  renamingPercentage: number;
  mapping: number;
  mappingPercentage: number;
  split: number;
  splitPercentage: number;
  submit: number;
  submitPercentage: number;
  unclassified: number;
  unclassifiedPercentage: number;
}

const initialSelectRows = {
  displayCheckBox: false,
  exporting: false,
  rows: [] as string[],
};

const Feedbacks = (props: Props) => {
  const {
    status,
    onSortChange,
    metros,
    selectedMetros,
    setSelectedMetros,
    getAllMetros,
  } = useFetchDocuments();

  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [selectRows, setSelectRows] = useState(initialSelectRows);
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | any>(null);
  const [feedbackData, setFeedbackData] = useState<
    IFeedbackAnalyticsDetailed[]
  >([]);
  const [activeApiCalls, setActiveApiCalls] = useState(0);
  const lastFetchParamsRef = useRef<string | null>(null);

  const {
    fromDate: selectedFromDate,
    toDate: selectedToDate,
    range,
  } = useSelector((state: any) => ({
    fromDate:
      state.feedback?.selectedDateRange?.fromDate ||
      state.documents?.selectedDateRange?.fromDate,
    toDate:
      state.feedback?.selectedDateRange?.toDate ||
      state.documents?.selectedDateRange?.toDate,
    range: state.feedback?.selectedDateRange?.range || "week",
  }));


  

  const rangeOptions = [
    { label: "Day", value: "day" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
    { label: "Total", value: "total" },
  ];


  const handleDateRangeChange = (
    dates: any,
    dateStrings: any,
    newRange: "day" | "week" | "month" | "total"
  ) => {
    let newFromDate: string | null = null;
    let newToDate: string | null = null;

    if (
      selectedFromDate === newFromDate &&
      selectedToDate === newToDate &&
      range === newRange
    ) {
      return; // Avoid redundant dispatch
    }

    if (newRange !== "total") {
      if (dates && dates[0] && dates[1]) {
        newFromDate = moment(dates[0].$d).format("YYYY-MM-DD HH:mm:ss");
        newToDate = moment(dates[1].$d).format("YYYY-MM-DD HH:mm:ss");
      } else {
        const todayClone = moment();
        switch (newRange) {
          case "day":
            newFromDate = todayClone
              .startOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            newToDate = todayClone.endOf("day").format("YYYY-MM-DD HH:mm:ss");
            break;
          case "week":
            newFromDate = todayClone
              .startOf("isoWeek")
              .format("YYYY-MM-DD HH:mm:ss");
            newToDate = todayClone
              .endOf("isoWeek")
              .format("YYYY-MM-DD HH:mm:ss");
            break;
          case "month":
            newFromDate = todayClone
              .startOf("month")
              .format("YYYY-MM-DD HH:mm:ss");
            newToDate = todayClone.endOf("month").format("YYYY-MM-DD HH:mm:ss");
            break;
        }
      }
    }

    dispatch(
      setFeedbackDateRange({
        fromDate: newFromDate,
        toDate: newToDate,
        range: newRange,
      })
    );
  };

  const fetchFeedbackData = async () => {
    setActiveApiCalls((prev) => prev + 1);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let params: Record<string, any> = {
        timezone,
        metros: selectedMetros,
      };

      if (selectedFromDate && selectedToDate && range !== "total") {
        let fromDateTime = moment(selectedFromDate).format(
          "YYYY-MM-DD HH:mm:ss"
        );
        let toDateTime = moment(selectedToDate).format("YYYY-MM-DD HH:mm:ss");

        switch (range) {
          case "day":
            fromDateTime = moment(selectedFromDate)
              .startOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            toDateTime = moment(selectedToDate)
              .endOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            break;
          case "week":
            fromDateTime = moment(selectedFromDate)
              .startOf("isoWeek")
              .format("YYYY-MM-DD HH:mm:ss");
            toDateTime = moment(selectedToDate)
              .endOf("isoWeek")
              .format("YYYY-MM-DD HH:mm:ss");
            break;
          case "month":
            fromDateTime = moment(selectedFromDate)
              .startOf("month")
              .format("YYYY-MM-DD HH:mm:ss");
            toDateTime = moment(selectedToDate)
              .endOf("month")
              .format("YYYY-MM-DD HH:mm:ss");
            break;
        }

        params.fromDate = fromDateTime;
        params.toDate = toDateTime;
      }

      if (range) params.range = range;

      const response = await feedbackService.getFeedbackAnalyticsDetailed(
        params
      );

      const formattedData: IFeedbackAnalyticsDetailed[] = [];

      if (response.data.data) {
        Object.keys(response.data.data).forEach((metro, index) => {
          const metroData = response.data.data[metro];
          formattedData.push({
            key: index,
            metro: metro.charAt(0).toUpperCase() + metro.slice(1),
            totalDocuments: metroData.totalDocuments || 0,
            totalPlaceholders: metroData.totalPlaceholders || 0,
            lowConf: metroData.LOW_CONF || 0,
            lowConfPercentage: metroData.LOW_CONF_PERCENTAGE || 0,
            renaming: metroData.PH_DOCUMENT_NAME_INCORRECT || 0,
            renamingPercentage:
              metroData.PH_DOCUMENT_NAME_INCORRECT_PERCENTAGE || 0,
            mapping: metroData.PLACEHOLDER_MAPPING_INCORRECT || 0,
            mappingPercentage:
              metroData.PLACEHOLDER_MAPPING_INCORRECT_PERCENTAGE || 0,
            split: metroData.SPLIT_INCORRECT || 0,
            splitPercentage: metroData.SPLIT_INCORRECT_PERCENTAGE || 0,
            submit: metroData.SUBMIT_INCORRECT || 0,
            submitPercentage: metroData.SUBMIT_INCORRECT_PERCENTAGE || 0,
            unclassified: metroData.UNCLASSIFIED || 0,
            unclassifiedPercentage: metroData.UNCLASSIFIED_PERCENTAGE || 0,
          });
        });
      }

      setFeedbackData(formattedData);
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Failed to fetch feedback analytics",
        "error"
      );
    } finally {
      setActiveApiCalls((prev) => Math.max(prev - 1, 0));
    }
  };

  useEffect(() => {
    setIsLoadingFeedback(activeApiCalls > 0);
  }, [activeApiCalls]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentParams = JSON.stringify({
        selectedFromDate,
        selectedToDate,
        selectedMetros,
        range,
        metrosLength: metros.length,
      });

      if (lastFetchParamsRef.current === currentParams) return;
      lastFetchParamsRef.current = currentParams;

      if (metros.length === 0) {
        getAllMetros();
        return;
      }

      if (range === "total" || (selectedFromDate && selectedToDate)) {
        fetchFeedbackData();
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [selectedFromDate, selectedToDate, selectedMetros, range, metros]);

  const MemoizedDateRangePicker = useMemo(
    () => (
      <DateRangePicker
        rangeOptions={rangeOptions.filter((option) => option.value !== range)}
        onDateRangeChange={handleDateRangeChange}
        currentFromDate={selectedFromDate}
        currentToDate={selectedToDate}
        currentRange={range}
        defaultRangeType="week"
        style={{ height: "39px" }}
      />
    ),
    [range, selectedFromDate, selectedToDate]
  );

  const getColumns = () => {
    const baseColumns: TableProps<IFeedbackAnalyticsDetailed>["columns"] = [
      {
        title: (
          <div className="flex flex-column ">
            <div className="flex jcc">Metro</div>
            <MetroFilter metros={metros} onApply={setSelectedMetros} />
          </div>
        ),
        key: "metro",
        dataIndex: "metro",
        align: "center",
        className: "row-name",
      },
      {
        title: <>Total Documents</>,
        key: "totalDocuments",
        dataIndex: "totalDocuments",
        align: "center",
        className: "w-10",
        sorter: (a, b) => a.totalDocuments - b.totalDocuments,
      },
      {
        title: <>Total Placeholders</>,
        key: "totalPlaceholders",
        dataIndex: "totalPlaceholders",
        align: "center",
        className: "w-10",
        sorter: (a, b) => a.totalPlaceholders - b.totalPlaceholders,
      },
      {
        title: <>Net Feedback</>,
        key: "total",
        dataIndex: "total",
        align: "center",
        className: "w-10",
        sorter: (a, b) =>
          a.renaming +
          a.mapping +
          a.split +
          a.submit -
          (b.renaming + b.mapping + b.split + b.submit),
        render: (value: number, record: IFeedbackAnalyticsDetailed) => {
          const totalValue =
            (record.renaming || 0) +
            (record.mapping || 0) +
            (record.split || 0) +
            (record.submit || 0);
          const totalPercentage =
            (record.renamingPercentage || 0) +
            (record.mappingPercentage || 0) +
            (record.splitPercentage || 0) +
            (record.submitPercentage || 0);
          return `${totalValue} (${totalPercentage.toFixed(2)}%)`;
        },
      },
      ...(["renaming", "mapping", "split", "submit", "lowConf"] as const).map(
        (field): ColumnType<IFeedbackAnalyticsDetailed> => ({
          title: <>{field.charAt(0).toUpperCase() + field.slice(1)}</>,
          key: field,
          dataIndex: field,
          align: "center",
          className: "w-12",
          sorter: (a:any, b:any) => a[field] - b[field],
          render: (value: number, record: any) =>
            `${value || 0} (${(record[`${field}Percentage`] || 0).toFixed(
              2
            )}%)`,
        })
      ),
    ];

    return baseColumns;
  };

  const columns = useMemo(() => getColumns(), [metros, selectedMetros, range]);

  const formattedExportData = useMemo(() => {
    return feedbackData.map((item) => ({
      Metro: item.metro,
      "Total Documents": item.totalDocuments,
      "Total Placeholders": item.totalPlaceholders,
      "Net Feedback": item.renaming + item.mapping + item.split + item.submit,
      "Net Feedback Percentage": `${(
        item.renamingPercentage +
        item.mappingPercentage +
        item.splitPercentage +
        item.submitPercentage
      ).toFixed(2)}%`,
      Renaming: item.renaming,
      "Renaming Percentage": `${item.renamingPercentage.toFixed(2)}%`,
      Mapping: item.mapping,
      "Mapping Percentage": `${item.mappingPercentage.toFixed(2)}%`,
      Split: item.split,
      "Split Percentage": `${item.splitPercentage.toFixed(2)}%`,
      Submit: item.submit,
      "Submit Percentage": `${item.submitPercentage.toFixed(2)}%`,
      "Low Conf": item.lowConf,
      "Low Conf Percentage": `${item.lowConfPercentage.toFixed(2)}%`,
    }));
  }, [feedbackData]);

  return (
    <div className="dashboard-table">
      <div className="table-actions">
        <div className="flex gp">
          <div>{MemoizedDateRangePicker}</div>
          <div className="flex jcc">
            <ExportToExcel
              data={formattedExportData}
              fileName="feedbacks.xlsx"
            />
          </div>
        </div>
      </div>

      <div
        className="dashboard-table__container"
        ref={containerRef}
        style={{ position: "relative", height: "100%" }}
      >
        <div
          className="dashboard-table__content"
          style={{ flex: 1, transition: "all 0.3s ease", width: "100%" }}
        >
          <Table
            columns={columns}
            pagination={false}
            dataSource={feedbackData}
            loading={isLoadingFeedback}
            onChange={(pagination, filters, sorter) => {
              onSortChange(sorter);
            }}
            rowKey="key"
            sortDirections={["ascend", "descend", null]}
            showSorterTooltip={true}
            locale={{ emptyText: <Empty>No Data Found</Empty> }}
          />
        </div>
      </div>
    </div>
  );
};

export default Feedbacks;
